import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw } from "lucide-react";
import { API, http, adminHeaders } from "@/lib/api";
import { TOURS, YACHTS } from "@/data/content";
import { Wordmark } from "@/components/site/Logo";

const NAMES = Object.fromEntries([...TOURS.map((x) => [x.id, x.title]), ...YACHTS.map((x) => [x.id, x.title.pt]), ["custom", "Tour à medida"]]);
const STATUS = { pending: "Pendente", confirmed: "Confirmada", completed: "Concluída", cancelled: "Cancelada" };
const DEPOSIT = { none: "—", pending: "Pendente", paid: "Pago", failed: "Falhou", expired: "Expirado" };

const Row = ({ b, onStatus, onDelete }) => (
  <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-booking-row-${b.id}`}>
    <td className="py-4 pr-4"><p className="text-[#F9F9F9]">{b.name}</p><p className="text-xs text-zinc-400">{b.email} · {b.phone}</p></td>
    <td className="py-4 pr-4 text-zinc-200">{NAMES[b.experience_id] || b.experience_id}{b.message && <p className="text-xs text-zinc-400 max-w-[240px] truncate" title={b.message}>{b.message}</p>}</td>
    <td className="py-4 pr-4 text-zinc-200 whitespace-nowrap">{b.date} {b.time}</td>
    <td className="py-4 pr-4 text-zinc-200">{b.guests}</td>
    <td className="py-4 pr-4"><span className={`text-xs uppercase tracking-wider ${b.deposit_status === "paid" ? "text-emerald-400" : "text-zinc-400"}`}>{DEPOSIT[b.deposit_status] || b.deposit_status}</span></td>
    <td className="py-4 pr-4">
      <select value={b.status} onChange={(e) => onStatus(b.id, e.target.value)} className="lux-input !py-2 !px-3 text-sm w-36" data-testid={`admin-status-select-${b.id}`}>
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </td>
    <td className="py-4"><button onClick={() => onDelete(b.id)} className="p-2 text-zinc-400 hover:text-red-400" data-testid={`admin-delete-${b.id}`}><Trash2 size={16} /></button></td>
  </tr>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`${API}/admin/bookings`, { headers: adminHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    await http.patch(`${API}/admin/bookings/${id}`, { status }, { headers: adminHeaders() });
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Estado atualizado");
  };
  const onDelete = async (id) => {
    if (!window.confirm("Eliminar este pedido?")) return;
    await http.delete(`${API}/admin/bookings/${id}`, { headers: adminHeaders() });
    setItems((xs) => xs.filter((x) => x.id !== id));
  };
  const logout = () => { localStorage.removeItem("tdc_admin_token"); nav("/admin/login"); };
  const shown = filter === "all" ? items : items.filter((x) => x.status === filter);
  const count = (s) => items.filter((x) => x.status === s).length;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F9F9F9]" data-testid="admin-dashboard">
      <header className="border-b border-white/5 px-5 md:px-10 py-5 flex items-center justify-between">
        <Wordmark />
        <div className="flex gap-2">
          <button onClick={load} className="btn-ghost !px-4" data-testid="admin-refresh-btn"><RefreshCw size={15} /></button>
          <button onClick={logout} className="btn-ghost !px-4" data-testid="admin-logout-btn"><LogOut size={15} /></button>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-5 md:px-10 py-12">
        <p className="eyebrow">Área reservada</p>
        <h1 className="font-display text-5xl mt-2">Pedidos de reserva</h1>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["Total", items.length], ["Pendentes", count("pending")], ["Confirmadas", count("confirmed")], ["Sinal pago", items.filter((x) => x.deposit_status === "paid").length]].map(([l, v]) => (
            <div key={l} className="glass-card p-6" data-testid={`admin-stat-${l.toLowerCase().replace(" ", "-")}`}>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">{l}</p>
              <p className="font-display text-5xl text-[#E5C158] mt-2">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-2">
          {[["all", "Todos"], ...Object.entries(STATUS)].map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
              className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${filter === k ? "bg-[#C5A059] text-black border-[#C5A059]" : "border-white/15 text-zinc-300 hover:border-[#C5A059]/60"}`}>{v}</button>
          ))}
        </div>
        <div className="mt-6 glass-card p-4 md:p-6 overflow-x-auto">
          <table className="w-full text-sm text-left min-w-[860px]">
            <thead><tr className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              <th className="pb-3">Cliente</th><th className="pb-3">Experiência</th><th className="pb-3">Data</th><th className="pb-3">Pax</th><th className="pb-3">Sinal</th><th className="pb-3">Estado</th><th />
            </tr></thead>
            <tbody>{shown.map((b) => <Row key={b.id} b={b} onStatus={onStatus} onDelete={onDelete} />)}</tbody>
          </table>
          {shown.length === 0 && <p className="py-10 text-center text-zinc-400" data-testid="admin-empty">Sem pedidos.</p>}
        </div>
      </main>
    </div>
  );
}
